import { navbarData } from '@core/layout/sidenav/model/sidenav-data';
import { UserPermissionsInterface } from '@core/models/authentication.model';

/**
 * Updates navigation menu access based on user permissions
 * @param permissions - Array of user permissions
 */
export function updateNavigationAccess(
  permissions: UserPermissionsInterface[]
): void {
  if (!permissions?.length) return;

  const menuPermissions = permissions.filter(
    permission => permission.display_in_menu
  );

  menuPermissions.forEach(permission => {
    navbarData.forEach(section => {
      // Check main section access
      if (section.routeLink === permission.slug) {
        section.hasAccess = true;
      }

      // Check subsection access
      if (section.items?.length) {
        section.items.forEach(item => {
          if (item.routeLink === permission.slug) {
            section.hasAccess = true;
            item.hasAccess = true;
          }
        });
      }
    });
  });
}

/**
 * Checks if the current route is accessible based on user permissions
 * @param currentUrl - The current route URL
 * @param permissions - Array of user permissions
 * @returns True if user has permission for the route
 */
export function hasRoutePermission(
  permissions: UserPermissionsInterface[],
  currentUrl: string
): boolean {
  return permissions.some(permission => {
    return normalizeUrlForComparison(permission.slug, currentUrl);
  });
}

/**
 * Normalizes URLs for permission comparison, handling dynamic route parameters
 * @param currentUrl - The current route URL
 * @param permissionSlug - The permission slug pattern
 * @returns Normalized URL for comparison
 */
export function normalizeUrlForComparison(
  permissionSlug: string,
  currentUrl: string
): boolean {
  // Direct match
  if (currentUrl === permissionSlug) {
    return true;
  }

  // Handle dynamic routes (e.g., /user/:id)
  if (permissionSlug.includes(':id')) {
    const currentUrlParts = currentUrl.split('/');
    const slugParts = permissionSlug.split('/');

    // Must have same number of parts
    if (currentUrlParts.length !== slugParts.length) {
      return false;
    }

    // Check if all parts match (considering :id as wildcard)
    return slugParts.every((slugPart, index) => {
      return slugPart === ':id' || slugPart === currentUrlParts[index];
    });
  }

  return false;
}
